import React from "react";
import round from "lodash.round";
import { AreaChart, CreditCard, DollarSign, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";

import { type ResolvedRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";
import { useCheckoutSessionStats } from "~/modules/Dashboard/AppStats/hooks/useCheckoutSessionStats";
import { useOrdersData } from "~/modules/Dashboard/AppStats/hooks/useOrdersData";
import { StatCard } from "~/modules/Dashboard/AppStats/StatCard";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";

const priceFormatter = getPriceFormatter("PLN");

interface SalesSummaryProps {
  products: string[];
  range: DateRange;
  comparisonRange: ResolvedRange | null;
  /** Human label of the compared period, e.g. "1–4.08". */
  comparisonLabel: string;
}

export const SalesSummary = ({
  products,
  range,
  comparisonRange,
  comparisonLabel,
}: SalesSummaryProps): React.ReactElement => {
  const { t } = useTranslation("dashboard");
  const { views, viewsPrevious, sessionChange, conversionPrevious, conversion } = useCheckoutSessionStats(
    products,
    range,
    comparisonRange,
  );
  const { finalized, finalizedPrevious, totalAmount, totalAmountPrevious, totalAmountChangePercentage, salesChange } =
    useOrdersData(products, range, comparisonRange);

  // Conversion is already a percentage, so its change is in percentage points, not relative.
  const conversionChange = conversionPrevious !== null ? round(conversion - conversionPrevious, 1) : null;
  const hasComparison = comparisonRange !== null;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title={t("stats.revenue")}
        icon={<DollarSign size={16} />}
        value={priceFormatter.format(totalAmount)}
        previousValue={hasComparison ? priceFormatter.format(totalAmountPrevious) : null}
        comparisonLabel={comparisonLabel}
        change={totalAmountChangePercentage}
        changeUnit="%"
      />
      <StatCard
        title={t("stats.salesAndRenewals")}
        icon={<CreditCard size={16} />}
        value={`+${finalized.length}`}
        previousValue={hasComparison ? `+${finalizedPrevious.length}` : null}
        comparisonLabel={comparisonLabel}
        change={salesChange}
        changeUnit="%"
      />
      <StatCard
        title={t("stats.checkoutViews")}
        icon={<ShoppingCart size={16} />}
        value={String(views)}
        previousValue={viewsPrevious !== null ? String(viewsPrevious) : null}
        comparisonLabel={comparisonLabel}
        change={sessionChange}
        changeUnit="%"
      />
      <StatCard
        title={t("stats.conversion")}
        icon={<AreaChart size={16} />}
        value={`${conversion}%`}
        previousValue={conversionPrevious !== null ? `${conversionPrevious}%` : null}
        comparisonLabel={comparisonLabel}
        change={conversionChange}
        changeUnit="p.p."
      />
    </div>
  );
};
